// Staff waiting screen (roadmap #41), route /staff/dashboard. The staff keeps this open
// to receive calls in the browser: it registers the web device (staff-join on the shared
// socket + FCM token) and routes incoming-call notifications to the ringing screen.
// Mirrors the always-logged-in /#/dashboard behaviour of the previous client.
import { useEffect, useState } from 'react'
import BrandLockup from '../components/BrandLockup'
import { useNavigate } from 'react-router-dom'
import { socket, readyTheSocketConnection, onSocketConnected } from '../core/socket'
import { fetchIceConfig } from '../core/iceConfig'
import { globals } from '../core/globals'
import { getAuthUser } from '../core/auth'
import { staffLogout } from './staffAuth'
import { t } from '../i18n'
import {
  processSilentNotification,
  registerFcmToken,
  registerMessagingServiceWorker,
  subscribeForegroundMessages,
  subscribeServiceWorkerMessages,
} from './messaging'

const StaffDashboard = () => {
  const navigate = useNavigate()
  const user = getAuthUser()
  const [connected, setConnected] = useState(socket.connected)
  const [notificationsReady, setNotificationsReady] = useState(
    typeof Notification !== 'undefined' && Notification.permission === 'granted'
  )

  useEffect(() => {
    let cleanupSwMessages = () => {}

    const init = async () => {
      // ICE CONFIG IS NEEDED BEFORE THE FIRST CALL (THE PEER CONNECTION READS IT).
      fetchIceConfig()

      // READY THE SHARED SOCKET AND REGISTER THIS STAFF EMAIL (staff-join). RE-REGISTER ON
      // EVERY RECONNECT — THE SERVER ROUTES CALLS BY THE REGISTERED EMAIL.
      try {
        await readyTheSocketConnection()
      } catch (e) {
        console.log('socket not ready yet, will register on connect', e)
      }
      onSocketConnected()
      socket.on('connect', onSocketConnected)

      // REGISTER THE WEB PUSH TOKEN AND THE BACKGROUND SERVICE WORKER.
      if (user?.user_id) registerFcmToken(user.user_id)
      registerMessagingServiceWorker()

      // FOREGROUND PUSHES AND SERVICE-WORKER BRIDGE BOTH OPEN THE INCOMING-CALL SCREEN.
      subscribeForegroundMessages((path, opts) => navigate(path, opts))
      cleanupSwMessages = subscribeServiceWorkerMessages(
        (path, opts) => navigate(path, opts),
        () => {}
      )

      // DEV-ONLY HOOK SO LOCAL E2E CAN INJECT AN INCOMING-CALL PUSH (FCM WEB PUSH IS NOT
      // DELIVERED TO HEADLESS BROWSERS — THE SAME STUB THE RESILIENCE HARNESS USED FOR THE
      // STAFF SIMULATOR). IT EXERCISES THE REAL processSilentNotification PATH.
      if (import.meta.env.DEV) {
        ;(window as any).__staffSimulateIncoming = (data: any) =>
          processSilentNotification(data, (path, opts) => navigate(path, opts))
      }
    }
    init()

    const onConnect = () => setConnected(true)
    const onDisconnect = () => setConnected(false)
    socket.on('connect', onConnect)
    socket.on('disconnect', onDisconnect)

    return () => {
      socket.off('connect', onSocketConnected)
      socket.off('connect', onConnect)
      socket.off('disconnect', onDisconnect)
      cleanupSwMessages()
    }
  }, [])

  const enableNotifications = () => {
    if (typeof Notification === 'undefined') return
    Notification.requestPermission().then((permission) => {
      if (permission === 'granted') {
        setNotificationsReady(true)
        if (user?.user_id) registerFcmToken(user.user_id)
      }
    })
  }

  const onLogout = async () => {
    await staffLogout()
    navigate('/staff', { replace: true })
  }

  const staffName = user?.full_name || globals.email || ''

  return (
    <div className="cf-page">
      <div className="cf-card cf-staff-card">
        <BrandLockup />
        <div className={`cf-status-dot ${connected ? 'online' : 'offline'}`} />
        <h1 className="cf-title">{connected ? t.staffAvailable : t.staffReconnecting}</h1>
        <p className="cf-subtitle">
          {connected ? t.staffAvailableHint : t.staffReconnectingHint}
        </p>
        {staffName && <p className="cf-staff-name">{staffName}</p>}

        {!notificationsReady && (
          <div className="cf-help">
            {t.staffNotificationsHelp}
            <button className="cf-btn cf-btn-secondary" onClick={enableNotifications}>
              {t.staffEnableNotifications}
            </button>
          </div>
        )}

        <button className="cf-btn cf-btn-secondary cf-staff-logout" onClick={onLogout}>
          {t.staffLogout}
        </button>
      </div>
    </div>
  )
}

export default StaffDashboard
